import { UIButton } from '../primitives';
import { useTranslation } from 'react-i18next';
import useConsentGuard from '../../hooks/useConsentGuard';

/**
 * CreditsBlock — «Кредитов на счету» карточка на Profile (Figma 318:5926).
 *
 * Geometry (identical to PlanBlock & ReferralBlock):
 *   FILL × HUG, cornerRadius 24, padding 24, itemSpacing 16,
 *   diagonal glass surface (rgba(255,255,255,0) → rgba(153,153,153,0.16))
 *   + 1px gradient stroke (rgba(102,102,102,0) → rgba(102,102,102,0.48)).
 *
 * Layout (top → bottom):
 *   1. title (FILL × HUG, itemSpacing=8)
 *      - header row (HORIZONTAL, SPACE_BETWEEN, h=32):
 *          * «Кредитов на счету» — Raleway Bold 16, text-secondary
 *          * «История» secondary sm uiButton, rightIcon=time (12px),
 *            label color = text-secondary (NOT text-primary), navigates to /history
 *      - count «9» — Raleway Bold 24 (NOT 32 — the old display-32 token is not used here)
 *   2. «Купить кредиты» — primary md uiButton (FILL × 40), leftIcon=plus (16),
 *      black-on-orange-gradient label.
 *
 * Props:
 *  - credits      (number) — остаток (0 показываем, не скрываем)
 *  - onBuyCredits () → void — navigate('/credits')
 *  - onHistory    () → void — navigate('/history')
 */
export default function CreditsBlock({
  credits = 0,
  onBuyCredits,
  onHistory,
  disabled = false,
}) {
  const { t } = useTranslation();
  const { requireConsent } = useConsentGuard();
  return (
    <div className="glass-card rounded-home-24 p-6 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        {/* header row: label + History button (right) */}
        <div className="flex items-center justify-between gap-2">
          <span className="font-sans font-bold text-h2 text-text-secondary">
            {t('profile.creditsOnAccount')}
          </span>
          {/* Figma 318:5926 «История» = secondary sm 101×32, padding 13/16,
              rightIcon=time at 12×12 (NOT the sm-default 18×18). Default
              UIButton sm has px-2 (=8) — override to px-4 (=16) to match
              Figma exactly; iconSize prop forces 12px icon. */}
          <UIButton
            type="secondary"
            size="sm"
            rightIcon="time"
            iconSize={12}
            onClick={() => requireConsent(onHistory, 'generate')}
            disabled={disabled}
            aria-label={t('profile.operationHistory')}
            className="!px-4 text-text-secondary"
          >
            {t('profile.history')}
          </UIButton>
        </div>

        {/* counter — 24/700 white. Figma node 194:7041 fontSize=24. */}
        <span
          className="font-sans font-bold text-[24px] leading-none text-text-primary"
          data-testid="credits-count"
        >
          {credits}
        </span>
      </div>

      <UIButton
        type="primary"
        size="md"
        leftIcon="plus"
        onClick={() => requireConsent(onBuyCredits, 'purchase')}
        disabled={disabled}
        aria-label={t('profile.buyCredits')}
        className="w-full"
      >
        {t('profile.buyCredits')}
      </UIButton>
    </div>
  );
}
